import type { SupportedLocale } from '@erp/shared';

export const logisticsOperationsTranslations = {
  de: {
    picking: {
      title: 'Kommissionierung',
      hint: 'Aufträge und offene Lieferscheine scannen, aufteilen und als Lieferentwurf vorbereiten.',
      new: 'Kommissionierung anlegen',
      source: 'Quellbeleg',
      worker: 'Zuständig',
      unassigned: 'Nicht zugewiesen',
      open: 'Öffnen',
      noTasks: 'Keine Kommissionierungen vorhanden.',
      retry: 'Erneut laden',
      loadError: 'Die Daten konnten nicht geladen werden.',
      saveError:
        'Speichern fehlgeschlagen. Eingaben bleiben erhalten; versuchen Sie denselben Vorgang erneut.',
      saveSuccess: 'Gespeichert',
      create: 'Anlegen',
      scan: 'Barcode / Artikelnummer',
      scanAction: 'Code prüfen',
      camera: 'Kamera verwenden',
      cameraUnsupported:
        'Kamerascannen wird in diesem Browser nicht unterstützt. Nutzen Sie den Scanner oder geben Sie den Code ein.',
      cameraDenied: 'Die Kamera ist nicht verfügbar. Scanner und manuelle Eingabe bleiben möglich.',
      stopCamera: 'Kamera schließen',
      sound: 'Bestätigungston',
      vibration: 'Vibration',
      scanHint:
        'USB-/Bluetooth-Scanner und manuelle Eingabe funktionieren mit Enter. Eine Codeprüfung bucht keinen Bestand.',
      candidate: 'Treffer auswählen',
      ambiguousHint:
        'Mehrere Artikel oder Gebinde verwenden diesen Code. Wählen Sie den richtigen Treffer ausdrücklich aus.',
      noMatch: 'Kein passender Artikel gefunden. Prüfen Sie den Code.',
      line: 'Position',
      requested: 'Soll',
      picked: 'Bestätigt',
      remaining: 'Offen',
      quantity: 'Menge',
      unit: 'Einheit',
      base: 'Basiseinheit',
      pack: 'Gebinde',
      location: 'Charge / Lager / Lagerplatz',
      unknownLocation: 'Historischer Lagerort nicht zugeordnet',
      unknownBatch: 'Keine Charge',
      available: 'Bestand',
      expiry: 'MHD / Ablauf',
      deviation: 'Begründung für abweichende Entnahme',
      confirm: 'Entnahme bestätigen',
      finish: 'Lieferentwurf vorbereiten',
      cancel: 'Kommissionierung abbrechen',
      reason: 'Begründung',
      shortageReason: 'Begründung der Fehlmenge',
      draft: 'Lieferentwurf öffnen',
      noPosting:
        'Scannen und Bestätigen buchen keinen Bestand. Erst der Versand des Lieferscheins erzeugt den Warenausgang.',
      history: 'Bestätigte Entnahmen',
      noAllocation: 'Noch keine Entnahme bestätigt.',
      shortage:
        'Fehlmengen bleiben im Ursprungsauftrag offen. Abschluss erfordert je Fehlmenge eine Begründung und das Recht für Abweichungen.',
      status: {
        OPEN: 'Offen',
        PICKING: 'In Arbeit',
        READY: 'Bereit',
        CLOSED: 'Vorbereitet',
        CANCELLED: 'Abgebrochen',
      },
      errors: {
        productRequired: 'Wählen Sie den Artikel oder prüfen Sie einen Code.',
        barcode: 'Der Barcode ist ungültig oder enthält nicht unterstützte GS1-Angaben.',
        expiryMismatch: 'Die Haltbarkeitsdaten im Barcode passen nicht zur Charge.',
        version:
          'Der Vorgang wurde zwischenzeitlich geändert. Laden Sie ihn neu und prüfen Sie die Eingaben.',
        source: 'Wählen Sie einen offenen Auftrag oder bearbeitbaren Lieferschein.',
        activeTask: 'Zu diesem Beleg besteht bereits eine offene Kommissionierung.',
        empty: 'Es sind keine bestandsgeführten, offenen Positionen bestätigt.',
        state: 'Diese Aktion ist im aktuellen Status nicht zulässig.',
        batchRequired: 'Wählen Sie eine Charge.',
        batchUnsuitable:
          'Die Charge ist für Status, Lieferdatum, Restlaufzeit oder Lager nicht geeignet.',
        stock: 'Der verfügbare Bestand reicht nicht aus.',
        locationStock: 'Der Bestand am gewählten Lagerplatz reicht nicht aus.',
        ambiguous: 'Wählen Sie den Artikel und das Gebinde ausdrücklich aus.',
        unknownCode: 'Der Code passt zu keinem auswählbaren Artikel.',
        wrongProduct: 'Der gescannte Artikel gehört nicht zur Position.',
        quantity: 'Die Menge muss positiv sein und darf die offene Menge nicht überschreiten.',
        override: 'Für eine Abweichung sind das gesonderte Recht und eine Begründung erforderlich.',
        shortageReason: 'Begründen Sie jede Fehlmenge.',
        reason: 'Geben Sie eine Begründung an.',
      },
    },
    inventorySessions: {
      addLocationTitle: 'Zusätzlichen Lagerplatz zählen',
      addLocationHint:
        'Wählen Sie einen bereits gesperrten Artikel mit Charge. Ein neuer Lagerplatz ergänzt eine Zählzeile mit dem tatsächlichen Journalbestand, ohne eine Warenbuchung.',
      addLine: 'Zählzeile hinzufügen',
      justify: 'Abweichungsgrund speichern',
      assignmentTitle: 'Historischen Bestand einem Lagerplatz zuordnen',
      assignmentHint:
        'Ordnen Sie nur physisch geprüften, bisher nicht zugeordneten Bestand zu. Eine nachvollziehbare Umbuchung verändert den Gesamtbestand nicht.',
      unknownAvailable: 'Verfügbarer, bisher nicht zugeordneter Bestand',
      targetBin: 'Ziellagerplatz',
      assign: 'Bestand zuordnen',
      assignmentSuccess: 'Bestand wurde zugeordnet',
      stockState: 'Bestandszustand',
      noBatch: 'Keine Charge',
      selectionHint: 'Auswahl beibehalten; diese Artikelauswahl ist auf die Suche begrenzt.',
      title: 'Inventur',
      hint: 'Gezielte Blindzählung mit Prüfung, Nachzählung und nachvollziehbarer Differenzbuchung.',
      new: 'Inventur starten',
      name: 'Bezeichnung',
      date: 'Zähltag',
      products: 'Artikel auswählen',
      search: 'Artikel suchen',
      productGroup: 'Artikelgruppe',
      batches: 'Charge eingrenzen',
      allProducts: 'Alle bestandsgeführten Artikel',
      warehouse: 'Lager',
      bin: 'Lagerplatz',
      allLocations: 'Alle Lagerorte',
      blind: 'Blindzählung',
      freezeHint:
        'Die Inventur sperrt alle betroffenen Artikel vollständig für Bestandsbewegungen, Reservierungen und Chargenpflege – auch bei einer Eingrenzung auf einen Lagerplatz. Freigabe erfolgt nach Abschluss oder Abbruch.',
      noTasks: 'Keine Inventuren vorhanden.',
      counted: 'Gezählt',
      uncounted: 'Nicht gezählt',
      expected: 'Sollbestand',
      difference: 'Differenz',
      count: 'Zählung speichern',
      round: 'Zählung {{round}}',
      review: 'Zur Prüfung geben',
      recount: 'Nachzählung anfordern',
      close: 'Inventur freigeben und buchen',
      cancel: 'Inventur abbrechen',
      export: 'Differenzprotokoll herunterladen',
      unknownGoods: 'Unbekannte Ware zur Klärung erfassen',
      description: 'Beschreibung',
      addClarification: 'Klärfall erfassen',
      resolve: 'Klärung abschließen',
      resolution: 'Ergebnis der Klärung',
      resolved: 'Geklärt',
      pending: 'Offen',
      emptyScope: 'Wählen Sie Artikel, Artikelgruppe, Charge oder Lager aus.',
      zeroHint: 'Null ist eine bewusste Zählung. Ein leeres Feld bedeutet nicht gezählt.',
      blindHint: 'Sollbestand und Differenz sind bis zur Prüfung verborgen.',
      unknownHint:
        'Historisch nicht zugeordnete Lagerorte bleiben ausdrücklich unbekannt. Ordnen Sie diese Mengen nicht ohne separate Bestandsklärung einem Lagerplatz zu.',
      countHistory: 'Zählhistorie',
      scanUnknown:
        'Der Code gehört nicht eindeutig zum Zählumfang. Wählen Sie eine Position oder erfassen Sie einen Klärfall.',
      status: {
        OPEN: 'Offen',
        COUNTING: 'Zählung',
        REVIEW: 'Prüfung',
        CLOSED: 'Abgeschlossen',
        CANCELLED: 'Abgebrochen',
      },
      errors: {
        scope: 'Wählen Sie einen gültigen, begrenzten Zählumfang aus.',
        warehouseRequired: 'Ein Lagerplatz benötigt ein Lager.',
        location: 'Lager oder Lagerplatz ist nicht aktiv.',
        empty: 'Der Umfang enthält keine zählbaren Bestände.',
        version: 'Die Inventur wurde geändert. Laden Sie sie neu.',
        state: 'Diese Aktion ist im aktuellen Status nicht zulässig.',
        recountReason: 'Begründen Sie eine erneute Zählung.',
        uncounted:
          'Alle Positionen müssen ausdrücklich gezählt werden, einschließlich Nullbeständen.',
        clarification: 'Schließen Sie zuerst alle offenen Klärfälle ab.',
        changedStock:
          'Der Sollbestand wurde trotz Sperre geändert. Eine neue fachliche Prüfung ist erforderlich.',
        varianceReason: 'Begründen Sie jede Differenz vor dem Abschluss.',
        unknownBatch: 'Nicht zugeordneter Chargenbestand benötigt eine separate fachliche Klärung.',
        blindExport: 'Das Differenzprotokoll ist erst ab der Prüfung verfügbar.',
      },
    },
  },
  en: {
    picking: {
      title: 'Picking',
      hint: 'Scan and split orders or open delivery notes to prepare delivery drafts.',
      new: 'Create picking task',
      source: 'Source document',
      worker: 'Assigned to',
      unassigned: 'Unassigned',
      open: 'Open',
      noTasks: 'No picking tasks yet.',
      retry: 'Reload',
      loadError: 'The data could not be loaded.',
      saveError: 'Saving failed. Your entries are retained; retry the same action.',
      saveSuccess: 'Saved',
      create: 'Create',
      scan: 'Barcode / article number',
      scanAction: 'Check code',
      camera: 'Use camera',
      cameraUnsupported:
        'Camera scanning is not supported in this browser. Use a scanner or enter the code.',
      cameraDenied: 'The camera is unavailable. Scanner and manual entry remain available.',
      stopCamera: 'Close camera',
      sound: 'Confirmation sound',
      vibration: 'Vibration',
      scanHint:
        'USB/Bluetooth scanners and manual entry work with Enter. Checking a code does not post stock.',
      candidate: 'Select match',
      ambiguousHint:
        'Several products or packs use this code. Select the correct match explicitly.',
      noMatch: 'No matching product found. Check the code.',
      line: 'Line',
      requested: 'Required',
      picked: 'Confirmed',
      remaining: 'Remaining',
      quantity: 'Quantity',
      unit: 'Unit',
      base: 'Base unit',
      pack: 'Pack',
      location: 'Batch / warehouse / bin',
      unknownLocation: 'Historical location is unassigned',
      unknownBatch: 'No batch',
      available: 'Stock',
      expiry: 'Best-before / expiry',
      deviation: 'Reason for a different allocation',
      confirm: 'Confirm pick',
      finish: 'Prepare delivery draft',
      cancel: 'Cancel picking',
      reason: 'Reason',
      shortageReason: 'Shortfall reason',
      draft: 'Open delivery draft',
      noPosting:
        'Scanning and confirmation do not post stock. Shipping the delivery note posts goods issue.',
      history: 'Confirmed picks',
      noAllocation: 'No pick confirmed yet.',
      shortage:
        'Shortfalls remain open on the original order. Completion requires a reason for each shortfall and deviation permission.',
      status: {
        OPEN: 'Open',
        PICKING: 'In progress',
        READY: 'Ready',
        CLOSED: 'Prepared',
        CANCELLED: 'Cancelled',
      },
      errors: {
        productRequired: 'Select a product or check a code.',
        barcode: 'The barcode is invalid or contains unsupported GS1 data.',
        expiryMismatch: 'The barcode shelf-life dates do not match the batch.',
        version: 'This task changed. Reload it and review your entries.',
        source: 'Select an open order or editable delivery note.',
        activeTask: 'An open picking task already exists for this document.',
        empty: 'No open stock-managed lines have been confirmed.',
        state: 'This action is unavailable in the current status.',
        batchRequired: 'Select a batch.',
        batchUnsuitable:
          'The batch does not satisfy status, delivery date, shelf-life or warehouse requirements.',
        stock: 'Insufficient available stock.',
        locationStock: 'Insufficient stock in the selected bin.',
        ambiguous: 'Select the product and pack explicitly.',
        unknownCode: 'This code matches no selectable product.',
        wrongProduct: 'The scanned product does not belong to this line.',
        quantity: 'Quantity must be positive and cannot exceed the remaining quantity.',
        override: 'A separate permission and reason are required for a deviation.',
        shortageReason: 'Explain each shortfall.',
        reason: 'Enter a reason.',
      },
    },
    inventorySessions: {
      addLocationTitle: 'Count an additional location',
      addLocationHint:
        'Select a product and batch already frozen by this session. A new location adds a count row with its actual journal balance without posting stock.',
      addLine: 'Add count row',
      justify: 'Save variance reason',
      assignmentTitle: 'Assign historic stock to a location',
      assignmentHint:
        'Assign only physically verified stock with an unknown historic location. A recorded transfer leaves the total unchanged.',
      unknownAvailable: 'Available stock with an unknown location',
      targetBin: 'Target bin',
      assign: 'Assign stock',
      assignmentSuccess: 'Stock assigned',
      stockState: 'Stock state',
      noBatch: 'No batch',
      selectionHint: 'Selections remain active; this product list is limited to the search.',
      title: 'Inventory count',
      hint: 'Scoped blind counts with review, recount and traceable difference posting.',
      new: 'Start inventory count',
      name: 'Name',
      date: 'Count date',
      products: 'Select products',
      search: 'Search products',
      productGroup: 'Product group',
      batches: 'Limit to batch',
      allProducts: 'All stock-managed products',
      warehouse: 'Warehouse',
      bin: 'Bin',
      allLocations: 'All locations',
      blind: 'Blind count',
      freezeHint:
        'The inventory freezes all affected products for stock movements, reservations and batch maintenance, even when limited to one bin. Closing or cancelling releases the freeze.',
      noTasks: 'No inventory counts yet.',
      counted: 'Counted',
      uncounted: 'Uncounted',
      expected: 'Expected stock',
      difference: 'Difference',
      count: 'Save count',
      round: 'Count {{round}}',
      review: 'Submit for review',
      recount: 'Request recount',
      close: 'Approve and post inventory',
      cancel: 'Cancel inventory count',
      export: 'Download difference report',
      unknownGoods: 'Record unknown goods for clarification',
      description: 'Description',
      addClarification: 'Record clarification',
      resolve: 'Resolve clarification',
      resolution: 'Clarification result',
      resolved: 'Resolved',
      pending: 'Open',
      emptyScope: 'Select products, a product group, a batch or a warehouse.',
      zeroHint: 'Zero is an explicit count. An empty field means uncounted.',
      blindHint: 'Expected stock and differences remain hidden until review.',
      unknownHint:
        'Unassigned historical locations remain explicitly unknown. Do not assign these quantities to a bin without a separate stock reconciliation.',
      countHistory: 'Count history',
      scanUnknown:
        'This code does not uniquely match the counting scope. Select a line or record a clarification.',
      status: {
        OPEN: 'Open',
        COUNTING: 'Counting',
        REVIEW: 'Review',
        CLOSED: 'Closed',
        CANCELLED: 'Cancelled',
      },
      errors: {
        scope: 'Select a valid, bounded counting scope.',
        warehouseRequired: 'A bin requires a warehouse.',
        location: 'The warehouse or bin is inactive.',
        empty: 'No countable stock in this scope.',
        version: 'The inventory changed. Reload it.',
        state: 'This action is unavailable in the current status.',
        recountReason: 'Explain a repeated count.',
        uncounted: 'Every line must be explicitly counted, including zero stock.',
        clarification: 'Resolve all open clarifications first.',
        changedStock:
          'Expected stock changed despite the freeze. A new business review is required.',
        varianceReason: 'Explain each variance before closing.',
        unknownBatch: 'Unassigned batch stock needs a separate reconciliation.',
        blindExport: 'The difference report is available from review onwards.',
      },
    },
  },
  fr: {
    picking: {
      title: 'Préparation de commandes',
      hint: 'Scannez et répartissez les commandes ou bons de livraison ouverts pour préparer les brouillons.',
      new: 'Créer une préparation',
      source: 'Document source',
      worker: 'Responsable',
      unassigned: 'Non affecté',
      open: 'Ouvrir',
      noTasks: 'Aucune préparation.',
      retry: 'Recharger',
      loadError: 'Impossible de charger les données.',
      saveError:
        'Échec de l’enregistrement. Les saisies sont conservées ; réessayez la même action.',
      saveSuccess: 'Enregistré',
      create: 'Créer',
      scan: 'Code-barres / référence article',
      scanAction: 'Vérifier le code',
      camera: 'Utiliser la caméra',
      cameraUnsupported:
        'Ce navigateur ne permet pas le scan caméra. Utilisez un lecteur ou saisissez le code.',
      cameraDenied:
        'La caméra est indisponible. Le lecteur et la saisie manuelle restent possibles.',
      stopCamera: 'Fermer la caméra',
      sound: 'Son de confirmation',
      vibration: 'Vibration',
      scanHint:
        'Les lecteurs USB/Bluetooth et la saisie manuelle fonctionnent avec Entrée. La vérification ne mouvemente pas le stock.',
      candidate: 'Choisir le résultat',
      ambiguousHint:
        'Plusieurs articles ou conditionnements utilisent ce code. Choisissez explicitement le bon résultat.',
      noMatch: 'Aucun article correspondant. Vérifiez le code.',
      line: 'Ligne',
      requested: 'Requis',
      picked: 'Confirmé',
      remaining: 'Restant',
      quantity: 'Quantité',
      unit: 'Unité',
      base: 'Unité de base',
      pack: 'Conditionnement',
      location: 'Lot / dépôt / emplacement',
      unknownLocation: 'Emplacement historique non affecté',
      unknownBatch: 'Sans lot',
      available: 'Stock',
      expiry: 'DDM / expiration',
      deviation: 'Motif de prélèvement différent',
      confirm: 'Confirmer le prélèvement',
      finish: 'Préparer le brouillon de livraison',
      cancel: 'Annuler la préparation',
      reason: 'Motif',
      shortageReason: 'Motif du manque',
      draft: 'Ouvrir le brouillon de livraison',
      noPosting:
        'Le scan et la confirmation ne mouvementent pas le stock. L’expédition du bon de livraison génère la sortie.',
      history: 'Prélèvements confirmés',
      noAllocation: 'Aucun prélèvement confirmé.',
      shortage:
        'Les manques restent ouverts sur la commande. La clôture exige un motif par manque et le droit de dérogation.',
      status: {
        OPEN: 'Ouvert',
        PICKING: 'En cours',
        READY: 'Prêt',
        CLOSED: 'Préparé',
        CANCELLED: 'Annulé',
      },
      errors: {
        productRequired: 'Sélectionnez un article ou vérifiez un code.',
        barcode: 'Code invalide ou données GS1 non prises en charge.',
        expiryMismatch: 'Les dates du code ne correspondent pas au lot.',
        version: 'La préparation a changé. Rechargez et vérifiez les saisies.',
        source: 'Sélectionnez une commande ouverte ou un bon modifiable.',
        activeTask: 'Une préparation ouverte existe déjà pour ce document.',
        empty: 'Aucune ligne ouverte avec stock n’est confirmée.',
        state: 'Action indisponible dans cet état.',
        batchRequired: 'Sélectionnez un lot.',
        batchUnsuitable:
          'Le lot ne satisfait pas les exigences de statut, date, durée restante ou dépôt.',
        stock: 'Stock disponible insuffisant.',
        locationStock: 'Stock insuffisant à l’emplacement sélectionné.',
        ambiguous: 'Choisissez explicitement l’article et le conditionnement.',
        unknownCode: 'Aucun article sélectionnable pour ce code.',
        wrongProduct: 'L’article scanné ne correspond pas à cette ligne.',
        quantity: 'La quantité doit être positive et ne pas dépasser le restant.',
        override: 'Une dérogation nécessite un droit spécifique et un motif.',
        shortageReason: 'Justifiez chaque manque.',
        reason: 'Saisissez un motif.',
      },
    },
    inventorySessions: {
      addLocationTitle: 'Compter un emplacement supplémentaire',
      addLocationHint:
        'Choisissez un article et lot déjà bloqués par cet inventaire. Le nouvel emplacement ajoute une ligne avec le solde réel du journal sans mouvement de stock.',
      addLine: 'Ajouter une ligne de comptage',
      justify: 'Enregistrer le motif de l’écart',
      assignmentTitle: 'Affecter un stock historique à un emplacement',
      assignmentHint:
        'Affectez uniquement le stock physiquement vérifié dont l’emplacement historique est inconnu. Le transfert enregistré ne change pas le total.',
      unknownAvailable: 'Stock disponible sans emplacement affecté',
      targetBin: 'Emplacement cible',
      assign: 'Affecter le stock',
      assignmentSuccess: 'Stock affecté',
      stockState: 'État du stock',
      noBatch: 'Sans lot',
      selectionHint:
        'Les choix sont conservés ; cette liste d’articles est limitée à la recherche.',
      title: 'Inventaire',
      hint: 'Comptage aveugle ciblé, contrôle, recomptage et ajustements traçables.',
      new: 'Démarrer un inventaire',
      name: 'Intitulé',
      date: 'Date de comptage',
      products: 'Choisir les articles',
      search: 'Rechercher les articles',
      productGroup: 'Groupe d’articles',
      batches: 'Limiter au lot',
      allProducts: 'Tous les articles stockés',
      warehouse: 'Dépôt',
      bin: 'Emplacement',
      allLocations: 'Tous les emplacements',
      blind: 'Comptage aveugle',
      freezeHint:
        'L’inventaire bloque tous les articles concernés pour les mouvements, réservations et modifications des lots, même limité à un emplacement. La clôture ou l’annulation lève le blocage.',
      noTasks: 'Aucun inventaire.',
      counted: 'Compté',
      uncounted: 'Non compté',
      expected: 'Stock théorique',
      difference: 'Écart',
      count: 'Enregistrer le comptage',
      round: 'Comptage {{round}}',
      review: 'Soumettre au contrôle',
      recount: 'Demander un recomptage',
      close: 'Approuver et comptabiliser',
      cancel: 'Annuler l’inventaire',
      export: 'Télécharger les écarts',
      unknownGoods: 'Signaler des marchandises inconnues',
      description: 'Description',
      addClarification: 'Créer une clarification',
      resolve: 'Résoudre',
      resolution: 'Résultat de la clarification',
      resolved: 'Résolu',
      pending: 'Ouvert',
      emptyScope: 'Choisissez des articles, un groupe, un lot ou un dépôt.',
      zeroHint: 'Zéro est un comptage explicite. Un champ vide signifie non compté.',
      blindHint: 'Le stock théorique et les écarts restent masqués jusqu’au contrôle.',
      unknownHint:
        'Les emplacements historiques non affectés restent inconnus. Ne réaffectez pas ces quantités sans rapprochement séparé du stock.',
      countHistory: 'Historique des comptages',
      scanUnknown:
        'Le code ne correspond pas de manière unique au périmètre. Choisissez une ligne ou créez une clarification.',
      status: {
        OPEN: 'Ouvert',
        COUNTING: 'Comptage',
        REVIEW: 'Contrôle',
        CLOSED: 'Clôturé',
        CANCELLED: 'Annulé',
      },
      errors: {
        scope: 'Choisissez un périmètre valide et limité.',
        warehouseRequired: 'Un emplacement exige un dépôt.',
        location: 'Dépôt ou emplacement inactif.',
        empty: 'Aucun stock à compter dans ce périmètre.',
        version: 'L’inventaire a changé. Rechargez-le.',
        state: 'Action indisponible dans cet état.',
        recountReason: 'Justifiez le nouveau comptage.',
        uncounted: 'Chaque ligne doit être comptée explicitement, y compris les stocks nuls.',
        clarification: 'Résolvez toutes les clarifications ouvertes.',
        changedStock:
          'Le stock théorique a changé malgré le blocage. Un nouveau contrôle métier est requis.',
        varianceReason: 'Justifiez chaque écart avant clôture.',
        unknownBatch: 'Un stock de lots non affecté exige un rapprochement séparé.',
        blindExport: 'Le rapport des écarts est disponible à partir du contrôle.',
      },
    },
  },
  es: {
    picking: {
      title: 'Preparación de pedidos',
      hint: 'Escanee y divida pedidos o albaranes abiertos para preparar borradores de entrega.',
      new: 'Crear preparación',
      source: 'Documento de origen',
      worker: 'Responsable',
      unassigned: 'Sin asignar',
      open: 'Abrir',
      noTasks: 'No hay preparaciones.',
      retry: 'Recargar',
      loadError: 'No se pudieron cargar los datos.',
      saveError: 'Error al guardar. Las entradas se conservan; repita la misma acción.',
      saveSuccess: 'Guardado',
      create: 'Crear',
      scan: 'Código de barras / referencia',
      scanAction: 'Comprobar código',
      camera: 'Usar cámara',
      cameraUnsupported:
        'Este navegador no admite escaneo con cámara. Use un lector o escriba el código.',
      cameraDenied:
        'La cámara no está disponible. El lector y la entrada manual siguen disponibles.',
      stopCamera: 'Cerrar cámara',
      sound: 'Sonido de confirmación',
      vibration: 'Vibración',
      scanHint:
        'Los lectores USB/Bluetooth y la entrada manual funcionan con Intro. Comprobar un código no contabiliza existencias.',
      candidate: 'Seleccionar coincidencia',
      ambiguousHint:
        'Varios artículos o envases usan este código. Seleccione explícitamente la coincidencia correcta.',
      noMatch: 'No se encontró ningún artículo. Compruebe el código.',
      line: 'Línea',
      requested: 'Necesario',
      picked: 'Confirmado',
      remaining: 'Pendiente',
      quantity: 'Cantidad',
      unit: 'Unidad',
      base: 'Unidad base',
      pack: 'Envase',
      location: 'Lote / almacén / ubicación',
      unknownLocation: 'Ubicación histórica sin asignar',
      unknownBatch: 'Sin lote',
      available: 'Existencias',
      expiry: 'Consumo preferente / caducidad',
      deviation: 'Motivo de la asignación diferente',
      confirm: 'Confirmar extracción',
      finish: 'Preparar borrador de entrega',
      cancel: 'Cancelar preparación',
      reason: 'Motivo',
      shortageReason: 'Motivo de la falta',
      draft: 'Abrir borrador de entrega',
      noPosting:
        'Escanear y confirmar no contabiliza existencias. El envío del albarán genera la salida.',
      history: 'Extracciones confirmadas',
      noAllocation: 'No hay extracciones confirmadas.',
      shortage:
        'Las faltas siguen pendientes en el pedido. La finalización requiere un motivo por falta y permiso de excepción.',
      status: {
        OPEN: 'Abierto',
        PICKING: 'En curso',
        READY: 'Listo',
        CLOSED: 'Preparado',
        CANCELLED: 'Cancelado',
      },
      errors: {
        productRequired: 'Seleccione un artículo o compruebe un código.',
        barcode: 'Código inválido o datos GS1 no admitidos.',
        expiryMismatch: 'Las fechas del código no coinciden con el lote.',
        version: 'La preparación cambió. Recargue y revise las entradas.',
        source: 'Seleccione un pedido abierto o albarán editable.',
        activeTask: 'Ya existe una preparación abierta para este documento.',
        empty: 'No se confirmó ninguna línea abierta con existencias.',
        state: 'Acción no disponible en este estado.',
        batchRequired: 'Seleccione un lote.',
        batchUnsuitable: 'El lote no cumple los requisitos de estado, fecha, vida útil o almacén.',
        stock: 'Existencias disponibles insuficientes.',
        locationStock: 'Existencias insuficientes en la ubicación elegida.',
        ambiguous: 'Seleccione explícitamente el artículo y el envase.',
        unknownCode: 'El código no corresponde a ningún artículo seleccionable.',
        wrongProduct: 'El artículo escaneado no pertenece a esta línea.',
        quantity: 'La cantidad debe ser positiva y no superar lo pendiente.',
        override: 'Una excepción requiere un permiso específico y un motivo.',
        shortageReason: 'Explique cada falta.',
        reason: 'Indique un motivo.',
      },
    },
    inventorySessions: {
      addLocationTitle: 'Contar otra ubicación',
      addLocationHint:
        'Seleccione un artículo y lote ya bloqueados por el inventario. La ubicación añade una línea con el saldo real del diario sin contabilizar stock.',
      addLine: 'Añadir línea de conteo',
      justify: 'Guardar motivo de diferencia',
      assignmentTitle: 'Asignar existencias históricas a una ubicación',
      assignmentHint:
        'Asigne solo existencias verificadas físicamente cuya ubicación histórica sea desconocida. El traslado registrado mantiene el total.',
      unknownAvailable: 'Existencias disponibles sin ubicación asignada',
      targetBin: 'Ubicación de destino',
      assign: 'Asignar existencias',
      assignmentSuccess: 'Existencias asignadas',
      stockState: 'Estado del stock',
      noBatch: 'Sin lote',
      selectionHint: 'La selección se conserva; esta lista de artículos se limita a la búsqueda.',
      title: 'Inventario',
      hint: 'Conteo ciego por ámbito con revisión, reconteo y ajustes trazables.',
      new: 'Iniciar inventario',
      name: 'Nombre',
      date: 'Fecha de conteo',
      products: 'Seleccionar artículos',
      search: 'Buscar artículos',
      productGroup: 'Grupo de artículos',
      batches: 'Limitar por lote',
      allProducts: 'Todos los artículos con existencias',
      warehouse: 'Almacén',
      bin: 'Ubicación',
      allLocations: 'Todas las ubicaciones',
      blind: 'Conteo ciego',
      freezeHint:
        'El inventario bloquea todos los artículos afectados para movimientos, reservas y mantenimiento de lotes, incluso al limitarlo a una ubicación. El cierre o la cancelación elimina el bloqueo.',
      noTasks: 'No hay inventarios.',
      counted: 'Contado',
      uncounted: 'Sin contar',
      expected: 'Existencias teóricas',
      difference: 'Diferencia',
      count: 'Guardar conteo',
      round: 'Conteo {{round}}',
      review: 'Enviar a revisión',
      recount: 'Solicitar reconteo',
      close: 'Aprobar y contabilizar',
      cancel: 'Cancelar inventario',
      export: 'Descargar diferencias',
      unknownGoods: 'Registrar mercancía desconocida',
      description: 'Descripción',
      addClarification: 'Registrar aclaración',
      resolve: 'Resolver aclaración',
      resolution: 'Resultado de la aclaración',
      resolved: 'Resuelto',
      pending: 'Abierto',
      emptyScope: 'Seleccione artículos, un grupo, un lote o un almacén.',
      zeroHint: 'Cero es un conteo explícito. Un campo vacío significa sin contar.',
      blindHint: 'Las existencias teóricas y las diferencias se ocultan hasta la revisión.',
      unknownHint:
        'Las ubicaciones históricas sin asignar siguen siendo desconocidas. No asigne estas cantidades sin una conciliación separada.',
      countHistory: 'Historial de conteos',
      scanUnknown:
        'El código no coincide de forma única con el ámbito. Seleccione una línea o registre una aclaración.',
      status: {
        OPEN: 'Abierto',
        COUNTING: 'Conteo',
        REVIEW: 'Revisión',
        CLOSED: 'Cerrado',
        CANCELLED: 'Cancelado',
      },
      errors: {
        scope: 'Seleccione un ámbito válido y limitado.',
        warehouseRequired: 'Una ubicación requiere un almacén.',
        location: 'El almacén o la ubicación está inactivo.',
        empty: 'No hay existencias que contar en este ámbito.',
        version: 'El inventario cambió. Recárguelo.',
        state: 'Acción no disponible en este estado.',
        recountReason: 'Explique el nuevo conteo.',
        uncounted: 'Cada línea debe contarse explícitamente, incluido el stock cero.',
        clarification: 'Resuelva las aclaraciones abiertas primero.',
        changedStock: 'El stock teórico cambió pese al bloqueo. Se requiere una nueva revisión.',
        varianceReason: 'Explique cada diferencia antes del cierre.',
        unknownBatch: 'Las existencias de lotes sin asignar necesitan una conciliación separada.',
        blindExport: 'El informe de diferencias está disponible desde la revisión.',
      },
    },
  },
  tr: {
    picking: {
      title: 'Sipariş toplama',
      hint: 'Siparişleri veya açık irsaliyeleri tarayıp bölerek teslimat taslağı hazırlayın.',
      new: 'Toplama görevi oluştur',
      source: 'Kaynak belge',
      worker: 'Sorumlu',
      unassigned: 'Atanmamış',
      open: 'Aç',
      noTasks: 'Toplama görevi yok.',
      retry: 'Yeniden yükle',
      loadError: 'Veriler yüklenemedi.',
      saveError: 'Kayıt başarısız. Girişler korunur; aynı işlemi yeniden deneyin.',
      saveSuccess: 'Kaydedildi',
      create: 'Oluştur',
      scan: 'Barkod / ürün numarası',
      scanAction: 'Kodu kontrol et',
      camera: 'Kamera kullan',
      cameraUnsupported:
        'Bu tarayıcı kamera taramasını desteklemiyor. Okuyucu kullanın veya kodu girin.',
      cameraDenied: 'Kamera kullanılamıyor. Okuyucu ve manuel giriş kullanılabilir.',
      stopCamera: 'Kamerayı kapat',
      sound: 'Onay sesi',
      vibration: 'Titreşim',
      scanHint:
        'USB/Bluetooth okuyucular ve manuel giriş Enter ile çalışır. Kod kontrolü stok hareketi oluşturmaz.',
      candidate: 'Eşleşmeyi seç',
      ambiguousHint:
        'Birden fazla ürün veya ambalaj bu kodu kullanıyor. Doğru eşleşmeyi açıkça seçin.',
      noMatch: 'Eşleşen ürün bulunamadı. Kodu kontrol edin.',
      line: 'Kalem',
      requested: 'Gereken',
      picked: 'Onaylanan',
      remaining: 'Kalan',
      quantity: 'Miktar',
      unit: 'Birim',
      base: 'Temel birim',
      pack: 'Ambalaj',
      location: 'Parti / depo / raf',
      unknownLocation: 'Geçmiş depo yeri atanmamış',
      unknownBatch: 'Parti yok',
      available: 'Stok',
      expiry: 'Tavsiye edilen tüketim / son kullanma',
      deviation: 'Farklı tahsis nedeni',
      confirm: 'Toplamayı onayla',
      finish: 'Teslimat taslağı hazırla',
      cancel: 'Toplamayı iptal et',
      reason: 'Neden',
      shortageReason: 'Eksik miktar nedeni',
      draft: 'Teslimat taslağını aç',
      noPosting:
        'Tarama ve onay stok hareketi oluşturmaz. İrsaliyenin sevki stok çıkışı oluşturur.',
      history: 'Onaylanan toplamalar',
      noAllocation: 'Henüz toplama onaylanmadı.',
      shortage:
        'Eksikler kaynak siparişte açık kalır. Tamamlama için her eksiğe neden ve sapma yetkisi gerekir.',
      status: {
        OPEN: 'Açık',
        PICKING: 'Devam ediyor',
        READY: 'Hazır',
        CLOSED: 'Hazırlandı',
        CANCELLED: 'İptal edildi',
      },
      errors: {
        productRequired: 'Ürün seçin veya kodu kontrol edin.',
        barcode: 'Barkod geçersiz veya desteklenmeyen GS1 verisi içeriyor.',
        expiryMismatch: 'Barkoddaki tarihler partiyle eşleşmiyor.',
        version: 'Görev değişti. Yeniden yükleyip girişleri kontrol edin.',
        source: 'Açık sipariş veya düzenlenebilir irsaliye seçin.',
        activeTask: 'Bu belge için açık toplama görevi var.',
        empty: 'Stok yönetilen açık kalem onaylanmadı.',
        state: 'Bu işlem mevcut durumda kullanılamaz.',
        batchRequired: 'Parti seçin.',
        batchUnsuitable:
          'Parti durum, teslimat tarihi, raf ömrü veya depo koşullarını karşılamıyor.',
        stock: 'Kullanılabilir stok yetersiz.',
        locationStock: 'Seçilen raftaki stok yetersiz.',
        ambiguous: 'Ürün ve ambalajı açıkça seçin.',
        unknownCode: 'Kod seçilebilir ürünle eşleşmiyor.',
        wrongProduct: 'Taranan ürün bu kaleme ait değil.',
        quantity: 'Miktar pozitif olmalı ve kalan miktarı aşmamalıdır.',
        override: 'Sapma için özel yetki ve neden gerekir.',
        shortageReason: 'Her eksiği açıklayın.',
        reason: 'Neden girin.',
      },
    },
    inventorySessions: {
      addLocationTitle: 'Ek depo yerini say',
      addLocationHint:
        'Bu sayımda kilitlenmiş ürün ve parti seçin. Yeni konum gerçek günlük bakiyesiyle sayım kalemi ekler; stok hareketi oluşturmaz.',
      addLine: 'Sayım kalemi ekle',
      justify: 'Fark nedenini kaydet',
      assignmentTitle: 'Geçmiş stoku depo yerine ata',
      assignmentHint:
        'Yalnızca fiziksel olarak doğrulanmış ve geçmiş konumu bilinmeyen stoku atayın. Kaydedilen transfer toplam stoku değiştirmez.',
      unknownAvailable: 'Konumu atanmamış kullanılabilir stok',
      targetBin: 'Hedef raf',
      assign: 'Stok ata',
      assignmentSuccess: 'Stok atandı',
      stockState: 'Stok durumu',
      noBatch: 'Parti yok',
      selectionHint: 'Seçimler korunur; bu ürün listesi aramayla sınırlıdır.',
      title: 'Stok sayımı',
      hint: 'Kontrol, yeniden sayım ve izlenebilir fark kaydı içeren kapsamlı kör sayım.',
      new: 'Sayım başlat',
      name: 'Ad',
      date: 'Sayım tarihi',
      products: 'Ürün seç',
      search: 'Ürün ara',
      productGroup: 'Ürün grubu',
      batches: 'Partiyle sınırla',
      allProducts: 'Stok yönetilen tüm ürünler',
      warehouse: 'Depo',
      bin: 'Raf',
      allLocations: 'Tüm konumlar',
      blind: 'Kör sayım',
      freezeHint:
        'Sayım, tek rafla sınırlı olsa bile ilgili tüm ürünlerde stok hareketlerini, rezervasyonları ve parti bakımını durdurur. Kapatma veya iptal kilidi kaldırır.',
      noTasks: 'Sayım yok.',
      counted: 'Sayılan',
      uncounted: 'Sayılmamış',
      expected: 'Beklenen stok',
      difference: 'Fark',
      count: 'Sayımı kaydet',
      round: 'Sayım {{round}}',
      review: 'Kontrole gönder',
      recount: 'Yeniden sayım iste',
      close: 'Sayımı onayla ve kaydet',
      cancel: 'Sayımı iptal et',
      export: 'Fark raporunu indir',
      unknownGoods: 'Bilinmeyen ürünü açıklama için kaydet',
      description: 'Açıklama',
      addClarification: 'Açıklama kaydı oluştur',
      resolve: 'Açıklamayı sonuçlandır',
      resolution: 'Açıklama sonucu',
      resolved: 'Çözüldü',
      pending: 'Açık',
      emptyScope: 'Ürün, ürün grubu, parti veya depo seçin.',
      zeroHint: 'Sıfır açık bir sayımdır. Boş alan sayılmamış demektir.',
      blindHint: 'Beklenen stok ve farklar kontrole kadar gizlenir.',
      unknownHint:
        'Atanmamış geçmiş konumlar açıkça bilinmeyen olarak kalır. Ayrı stok mutabakatı olmadan bu miktarları bir rafa atamayın.',
      countHistory: 'Sayım geçmişi',
      scanUnknown:
        'Kod sayım kapsamıyla tekil eşleşmiyor. Kalem seçin veya açıklama kaydı oluşturun.',
      status: {
        OPEN: 'Açık',
        COUNTING: 'Sayılıyor',
        REVIEW: 'Kontrol',
        CLOSED: 'Kapalı',
        CANCELLED: 'İptal edildi',
      },
      errors: {
        scope: 'Geçerli ve sınırlı sayım kapsamı seçin.',
        warehouseRequired: 'Raf için depo gerekir.',
        location: 'Depo veya raf etkin değil.',
        empty: 'Bu kapsamda sayılacak stok yok.',
        version: 'Sayım değişti. Yeniden yükleyin.',
        state: 'Bu işlem mevcut durumda kullanılamaz.',
        recountReason: 'Tekrarlanan sayımı açıklayın.',
        uncounted: 'Sıfır stok dahil her kalem açıkça sayılmalıdır.',
        clarification: 'Önce açık açıklama kayıtlarını sonuçlandırın.',
        changedStock: 'Beklenen stok kilide rağmen değişti. Yeni iş kontrolü gerekir.',
        varianceReason: 'Kapatmadan önce her farkı açıklayın.',
        unknownBatch: 'Atanmamış parti stoku için ayrı mutabakat gerekir.',
        blindExport: 'Fark raporu kontrol aşamasından itibaren kullanılabilir.',
      },
    },
  },
} satisfies Record<SupportedLocale, { picking: object; inventorySessions: object }>;
